import React from 'react';
import {
  Rating as MuiRating,
  RatingProps as MuiRatingProps,
  Typography,
  Box,
} from '@mui/material';
import { Star, StarBorder, Favorite, FavoriteBorder } from '@mui/icons-material';
import { StyledRating, RatingContainer } from './Rating.style';

export interface RatingProps extends Omit<MuiRatingProps, 'onChange'> {
  /**
   * The current rating value
   */
  value?: number | null;
  /**
   * Callback fired when the rating changes
   */
  onChange?: (value: number | null) => void;
  /**
   * The maximum rating value
   */
  max?: number;
  /**
   * The precision of the rating
   */
  precision?: number;
  /**
   * Whether the rating is read-only
   */
  readOnly?: boolean;
  /**
   * Whether the rating is disabled
   */
  disabled?: boolean;
  /**
   * The size of the rating
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Label for the rating
   */
  label?: string;
  /**
   * Whether to show the value as text
   */
  showValue?: boolean;
  /**
   * Icon variant
   */
  variant?: 'star' | 'heart';
  /**
   * The color of the rating
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success';
}

export const Rating: React.FC<RatingProps> = ({
  value,
  onChange,
  max = 5,
  precision = 1,
  readOnly = false,
  disabled = false,
  size = 'medium',
  label,
  showValue = false,
  variant = 'star',
  color = 'warning',
  ...props
}) => {
  const handleChange = (event: React.SyntheticEvent, newValue: number | null) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const getIcons = () => {
    switch (variant) {
      case 'heart':
        return {
          icon: <Favorite />,
          emptyIcon: <FavoriteBorder />,
        };
      default:
        return {
          icon: <Star />,
          emptyIcon: <StarBorder />,
        };
    }
  };

  const icons = getIcons();

  return (
    <RatingContainer>
      {label && (
        <Typography variant="body2" gutterBottom>
          {label}
        </Typography>
      )}
      
      <Box display="flex" alignItems="center" gap={1}>
        <StyledRating
          value={value}
          onChange={handleChange}
          max={max}
          precision={precision}
          readOnly={readOnly}
          disabled={disabled}
          size={size}
          color={color}
          icon={icons.icon}
          emptyIcon={icons.emptyIcon}
          {...props}
        />
        
        {showValue && value !== null && value !== undefined && (
          <Typography variant="body2" color="textSecondary">
            {value.toFixed(precision < 1 ? 1 : 0)}/{max}
          </Typography>
        )}
      </Box>
    </RatingContainer>
  );
}; 